import type { ComponentProps } from 'react';
import { twMerge } from 'tailwind-merge';

import { CommunityCancerTypeFilter } from './CommunityCancerTypeFilter';
import { CommunitySortBy } from './CommunitySortBy';

import { View } from '@/components/ui/view';
import { type SortOption } from '@/constants/community';

type CommunityFilterRowProps = {
  /**
   * Saved posts are not filtered or sorted (the API ignores both), and the keyword search is
   * hidden there too, so the whole row is hidden rather than shown disabled.
   */
  showSavedPosts: boolean;
  sortBy: SortOption;
  onSortChange: (sortBy: SortOption) => void;
  /** At `lg` the row sits beside the search: a fixed-width picker and a compact text Sort. */
  compactSort?: boolean;
} & ComponentProps<typeof CommunityCancerTypeFilter>;

/**
 * Cancer type picker and sort side by side from `md`, stacked on narrower widths. The sort is
 * a fixed narrow width and the picker takes the rest of the row.
 */
export function CommunityFilterRow({
  showSavedPosts,
  sortBy,
  onSortChange,
  compactSort = false,
  ...cancerTypeFilterProps
}: CommunityFilterRowProps) {
  if (showSavedPosts) {
    return null;
  }

  return (
    <View
      className={twMerge(
        'w-full flex-col gap-4 md:flex-row md:items-center',
        compactSort && 'w-auto shrink-0 gap-3 md:gap-3'
      )}>
      <View className={compactSort ? 'w-[280px] flex-none' : 'w-full md:flex-1'}>
        <CommunityCancerTypeFilter
          {...cancerTypeFilterProps}
          fieldClassName={compactSort ? 'h-11' : undefined}
        />
      </View>

      <View className={compactSort ? 'w-auto flex-none' : 'w-full md:w-44'}>
        <CommunitySortBy
          value={sortBy}
          onChange={onSortChange}
          variant={compactSort ? 'compact' : 'default'}
        />
      </View>
    </View>
  );
}
